import {
  LocalOfferLocation,
  LocalOfferMarker,
} from '@varo/graphql-schema-types';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  Marker,
} from '@vis.gl/react-google-maps';
import { DynamicMapCardProps } from 'features/rewards/components/MapCard/DynamicMapCard.helper';
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { getWebIcon } from 'shared/components/maps/PredictionMapView/PredictionMapView.helpers';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import MapNode, { MapNodeProps } from 'shared/components/vxl/MapNode/MapNode';
import useClusteredMarkers from 'shared/hooks/maps/useClusteredMarkers';
import theme from 'shared/styles/theme';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import {
  DefaultMapZoom,
  WebMapControlProps,
} from 'shared/utils/maps/mapConstants';

const MARKER_ICON_MAP: Record<LocalOfferMarker, IconKeyType> = {
  [LocalOfferMarker.Merchant]: 'STORE',
  [LocalOfferMarker.Food]: 'FOOD',
  [LocalOfferMarker.Entertainment]: 'ENTERTAINMENT',
  [LocalOfferMarker.Travel]: 'TRAVEL',
  [LocalOfferMarker.Shopping]: 'SHOPPING',
  [LocalOfferMarker.UndefinedMarker]: 'location',
  [LocalOfferMarker.Gas]: 'GAS',
};

const getMarkerIcon = (marker: LocalOfferLocation): IconKeyType => {
  return MARKER_ICON_MAP[marker.marker] ?? 'location';
};

const mapMarkerToMapNodeProps = (
  marker: LocalOfferLocation,
  isSelected: boolean,
  onPress: () => void,
): MapNodeProps => ({
  iconKey: getMarkerIcon(marker),
  metric: marker.cashbackAmount ?? '0',
  labelText: marker.label ?? '',
  priceText: marker.label ?? '',
  selected: isSelected,
  analyticsId: marker.id,
  onPress,
});

const DynamicMapCard = ({
  id,
  markerData,
  cameraLocation,
  onMarkerPress,
  onMapCardPress,
  allowMapInteractions = false,
  usePillAnnotations = true,
  containerStyle = {},
}: DynamicMapCardProps) => {
  const [selectedMarker, setSelectedMarker] = useState<LocalOfferLocation>();
  const clusteredMarkers = useClusteredMarkers(markerData ?? []);

  // using mapId from google cloud config to enable custom map styling, with fallback to default map if not set
  const mapId = getEnvironmentVar('EXPO_CASHBACK_MAP_ID') ?? id;

  return (
    <View
      testID={`dynamic-map-card-container-${id}`}
      style={[baseStyles.viewContainer, containerStyle]}
    >
      <Pressable
        key={id}
        style={baseStyles.mapContainer}
        onPress={onMapCardPress}
        role="button"
      >
        {cameraLocation ? (
          <View style={baseStyles.mapWrapper}>
            <APIProvider
              apiKey={
                getEnvironmentVar('EXPO_GOOGLE_MAPS_WEB_API_KEY') as string
              }
            >
              <Map
                mapId={mapId}
                defaultCenter={{
                  lat: cameraLocation.lat,
                  lng: cameraLocation.lng,
                }}
                defaultZoom={DefaultMapZoom.l}
                reuseMaps
                style={baseStyles.map}
                {...WebMapControlProps}
              >
                {usePillAnnotations
                  ? clusteredMarkers.map((marker) => (
                      <AdvancedMarker
                        key={marker.id}
                        position={{
                          lat: marker.coordinate.latitude,
                          lng: marker.coordinate.longitude,
                        }}
                        onClick={() => {
                          setSelectedMarker(marker);
                          onMarkerPress(marker);
                        }}
                      >
                        <MapNode
                          {...mapMarkerToMapNodeProps(
                            marker,
                            selectedMarker?.id === marker.id,
                            () => {
                              setSelectedMarker(marker);
                              onMarkerPress(marker);
                            },
                          )}
                          hideIcon={
                            selectedMarker?.id !== marker.id &&
                            !!marker.hideIcon
                          }
                        />
                      </AdvancedMarker>
                    ))
                  : markerData?.map((marker) => (
                      <Marker
                        key={marker.id}
                        position={{
                          lat: marker.coordinate.latitude,
                          lng: marker.coordinate.longitude,
                        }}
                        icon={{
                          url:
                            'data:image/svg+xml;charset=utf-8,' +
                            encodeURIComponent(
                              getWebIcon(
                                marker,
                                selectedMarker?.id === marker.id,
                              ),
                            ),
                        }}
                        onClick={() => {
                          setSelectedMarker(marker);
                          onMarkerPress(marker);
                        }}
                      />
                    ))}
              </Map>
            </APIProvider>
            {/* Non-interactive overlay to block map gestures (scroll/zoom) */}
            {!allowMapInteractions ? (
              <View
                style={baseStyles.overlay}
                pointerEvents="auto"
                testID="mapNonInteractiveOverlay"
              />
            ) : null}
          </View>
        ) : null}
      </Pressable>
    </View>
  );
};

const baseStyles = StyleSheet.create({
  map: {
    elevation: -1,
    height: '100%',
    width: '100%',
    zIndex: -1,
    borderRadius: 4,
  },
  mapWrapper: {
    position: 'relative',
    height: '100%',
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
  },
  mapContainer: {
    alignItems: 'center',
    borderColor: theme.color.border.primary,
    borderRadius: 4,
    borderWidth: 1,
    elevation: -1,
    justifyContent: 'center',
    flex: 1,
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    backgroundColor: 'transparent',
  },
  viewContainer: {
    height: 260,
    width: '100%',
  },
});

export default DynamicMapCard;
